import { captureException, addBreadcrumb } from './sentry';
import { toast } from './toast.utils';

interface ErrorTracker {
  count: number;
  lastOccurrence: number;
  recoveryAttempts: number;
}

const errorTracker: ErrorTracker = {
  count: 0,
  lastOccurrence: 0,
  recoveryAttempts: 0,
};

const ERROR_RESET_INTERVAL = 60000;
const MAX_ERRORS_PER_INTERVAL = 5;
const DEBOUNCE_DELAY = 2000;

let lastNotificationTime = 0;
let recoveryInProgress = false;

/**
 * Detects if an error is related to IndexedDB
 */
export const isIndexedDBError = (error: any): boolean => {
  if (!error) return false;

  const errorMessage = (error.message || error.toString()).toLowerCase();
  const errorName = (error.name || '').toLowerCase();

  return (
    errorMessage.includes('indexeddb') ||
    errorMessage.includes('indexed database') ||
    errorMessage.includes('idbdatabase') ||
    errorMessage.includes('connection to indexed database server lost') ||
    errorMessage.includes('internal error was encountered in the indexed database server') ||
    errorName.includes('indexeddb') ||
    error.code === 'auth/internal-error'
  );
};

/**
 * Attempts to recover from IndexedDB errors
 */
const attemptRecovery = async (): Promise<boolean> => {
  if (recoveryInProgress) {
    console.info('Recovery already in progress, skipping...');
    return false;
  }

  recoveryInProgress = true;
  errorTracker.recoveryAttempts++;

  try {
    addBreadcrumb('indexeddb-recovery', 'Attempting IndexedDB recovery', {
      recoveryAttempt: errorTracker.recoveryAttempts,
      errorCount: errorTracker.count,
    });

    try {
      const databases = await indexedDB.databases();
      const firebaseDatabases = databases.filter(db => 
        db.name?.includes('firebase') || db.name?.includes('firebaseLocalStorage')
      );

      for (const db of firebaseDatabases) {
        if (db.name) {
          try {            
            await new Promise<void>((resolve) => {
              const request = indexedDB.deleteDatabase(db.name!);
              
              request.onsuccess = () => {
                console.info(`Successfully deleted Firebase database: ${db.name}`);
                resolve();
              };
              
              request.onerror = (event) => {
                console.warn(`Error deleting ${db.name}:`, event);
                resolve();
              };
              
              request.onblocked = () => {
                console.warn(`Blocked from deleting ${db.name}, database may be in use`);
                setTimeout(() => resolve(), 100);
              };
              
              setTimeout(() => {
                console.warn(`Timeout deleting ${db.name}`);
                resolve();
              }, 5000);
            });
          } catch (dbError) {
            console.warn(`Failed to delete database ${db.name}:`, dbError);
          }
        }
      }
    } catch (cleanupError) {
      console.warn('Failed to cleanup Firebase databases:', cleanupError);
    }

    console.info('IndexedDB recovery completed');
    recoveryInProgress = false;
    return true;
  } catch (error) {
    console.error('IndexedDB recovery failed:', error);
    captureException(error as Error, {
      extra: {
        component: 'indexedDBErrorHandler',
        action: 'attemptRecovery',
        recoveryAttempt: errorTracker.recoveryAttempts,
      },
    });
    
    recoveryInProgress = false;
    return false;
  }
};

/**
 * Shows user-friendly error notification
 */
const showRecoveryNotification = (shouldRefresh: boolean) => {
  const now = Date.now();
  
  if (now - lastNotificationTime < DEBOUNCE_DELAY) {
    return;
  }
  
  lastNotificationTime = now;

  if (shouldRefresh) {
    toast.error('Connection issue detected. Please refresh the page to continue.');
  } else {
    toast.error('A temporary connection issue occurred. Retrying...');
  }
};

/**
 * Handles IndexedDB errors globally
 */
export const handleIndexedDBError = async (error: any): Promise<void> => {
  if (!isIndexedDBError(error)) {
    return;
  }

  const now = Date.now();

  if (now - errorTracker.lastOccurrence > ERROR_RESET_INTERVAL) {
    errorTracker.count = 0;
    errorTracker.recoveryAttempts = 0;
  }

  errorTracker.count++;
  errorTracker.lastOccurrence = now;

  captureException(error, {
    extra: {
      component: 'indexedDBErrorHandler',
      errorCount: errorTracker.count,
      recoveryAttempts: errorTracker.recoveryAttempts,
      timeSinceLastError: now - errorTracker.lastOccurrence,
      userAgent: navigator.userAgent,
      documentHidden: document.hidden,
      onLine: navigator.onLine,
    },
  });

  addBreadcrumb('indexeddb-error', 'IndexedDB error detected', {
    errorCount: errorTracker.count,
    errorMessage: error?.message || String(error),
  });

  if (errorTracker.count > MAX_ERRORS_PER_INTERVAL) {
    console.error('Too many IndexedDB errors, prompting user to refresh');
    showRecoveryNotification(true);
    return;
  }

  console.warn('IndexedDB error detected, attempting recovery...');
  showRecoveryNotification(false);
  
  const recovered = await attemptRecovery();
  
  if (recovered) {
    addBreadcrumb('indexeddb-recovery', 'Recovery successful', {
      recoveryAttempt: errorTracker.recoveryAttempts,
    });
    
    setTimeout(() => {
      toast.info('Connection restored. Refresh recommended for best experience.');
    }, 1000);
  } else if (errorTracker.recoveryAttempts > 2) {
    showRecoveryNotification(true);
  }
};

let handlersInitialized = false;
const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
  if (event.reason) {
    handleIndexedDBError(event.reason);
  }
};

const errorHandler = (event: ErrorEvent) => {
  if (event.error) {
    handleIndexedDBError(event.error);
  }
};

export const initializeIndexedDBErrorHandlers = (): (() => void) => {
  if (handlersInitialized) {
    console.warn('IndexedDB error handlers already initialized');
    return () => {};
  }

  handlersInitialized = true;
  window.addEventListener('unhandledrejection', unhandledRejectionHandler);
  window.addEventListener('error', errorHandler);

  return () => {
    window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
    window.removeEventListener('error', errorHandler);
    handlersInitialized = false;
    console.info('IndexedDB error handlers cleaned up');
  };
};

/**
 * Get current error statistics (for debugging)
 */
export const getErrorStats = () => ({
  ...errorTracker,
  recoveryInProgress,
});
